import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { UserPreferences } from '@mem-ai-hub/shared';
import { DEFAULT_PREFERENCES } from '@mem-ai-hub/shared';
import { useAuth } from '../auth/useAuth';
import {
  getPreferences,
  updatePreferences as apiUpdatePreferences,
} from '../services/preferencesApi';

const STORAGE_KEY = 'aihub-preferences';

function loadCached(): UserPreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveCache(prefs: UserPreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    // Also sync the theme key used by the FOUC-prevention script
    localStorage.setItem('aihub-theme', prefs.theme);
  } catch { /* ignore */ }
}

interface PreferencesContextValue {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => void;
  isLoading: boolean;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(
  undefined,
);

const PLACEHOLDER: UserPreferences = {
  ...DEFAULT_PREFERENCES,
  id: '',
  userId: '',
  updatedAt: '',
};

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(
    () => loadCached() ?? PLACEHOLDER,
  );
  const [isLoading, setIsLoading] = useState(true);

  // Fetch from API once authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    async function load() {
      try {
        const prefs = await getPreferences();
        if (!cancelled) {
          setPreferences(prefs);
          saveCache(prefs);
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
        // Keep cached or default values
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const updatePreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPreferences((prev) => {
        const next = { ...prev, [key]: value };
        saveCache(next);
        return next;
      });

      // Persist to API (fire and forget with rollback on error)
      apiUpdatePreferences({ [key]: value } as Partial<UserPreferences>).catch(
        (err) => {
          console.error('Failed to save preference:', err);
          // Rollback: refetch from API
          getPreferences()
            .then((prefs) => {
              setPreferences(prefs);
              saveCache(prefs);
            })
            .catch(() => { /* give up */ });
        },
      );
    },
    [],
  );

  return (
    <PreferencesContext.Provider
      value={{ preferences, updatePreference, isLoading }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return ctx;
}
